"use client";

import {
  ArrowDownLeftIcon,
  ArrowUpRightIcon,
  BanknoteIcon,
  CoinsIcon,
  ImageIcon,
  ShieldCheckIcon,
  UserIcon,
  WalletIcon,
} from "lucide-react";
import { memo, useState } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { promptGenerators } from "@/hooks/use-quick-action";
import { cn } from "@/lib/utils";
import { AddressDisplay, truncateAddress } from "./address-display";
import { ChainBadge } from "./chain-badge";
import { CollapsibleCard } from "./collapsible-card";
import { QuickActionButton } from "./quick-action-button";
import { QuickActionsBar } from "./quick-actions-bar";
import type {
  QuickAction,
  TokenBalance,
  TokenTransfer,
  WalletOverviewProps,
  WalletState,
} from "./types";
import { getChainInfo } from "./types";
import { formatTokenValue, formatUsdValue } from "./value-display";

/**
 * Default number of tokens/transfers to show
 */
const DEFAULT_VISIBLE_TOKENS = 5;
const DEFAULT_VISIBLE_TRANSFERS = 5;

/**
 * Main WalletOverview component - displays wallet state with tokens and transfers
 */
const PureWalletOverview = ({
  cardId,
  input,
  output,
  state,
  onQuickAction,
  isReadonly,
}: WalletOverviewProps) => {
  const _chain = getChainInfo(input.chainId);

  // Handle loading states
  if (state === "input-streaming" || state === "input-available") {
    return (
      <WalletOverviewSkeleton
        addresses={input.walletAddresses}
        chainId={input.chainId}
      />
    );
  }

  // Handle error state
  if (output && "error" in output) {
    return (
      <WalletOverviewError
        addresses={input.walletAddresses}
        chainId={input.chainId}
        error={output.error}
      />
    );
  }

  // Handle empty output
  if (!output || output.length === 0) {
    return (
      <WalletOverviewError
        addresses={input.walletAddresses}
        chainId={input.chainId}
        error="No wallet data found"
      />
    );
  }

  // Render wallet states
  return (
    <div className="space-y-4">
      {output.map((wallet, idx) => (
        <WalletCard
          cardId={`${cardId}-wallet-${wallet.address || idx}`}
          chainId={input.chainId}
          key={wallet.address || idx}
          onQuickAction={onQuickAction}
          wallet={wallet}
        />
      ))}
    </div>
  );
};

export const WalletOverview = memo(PureWalletOverview);

/**
 * Individual wallet card
 */
type WalletCardProps = {
  cardId: string;
  wallet: WalletState;
  chainId: string;
  onQuickAction: WalletOverviewProps["onQuickAction"];
};

function WalletCard({
  cardId,
  wallet,
  chainId,
  onQuickAction,
}: WalletCardProps) {
  const [showAllTokens, setShowAllTokens] = useState(false);
  const [showAllTransfers, setShowAllTransfers] = useState(false);
  const chain = getChainInfo(chainId);

  const visibleTokens = showAllTokens
    ? wallet.tokens
    : wallet.tokens.slice(0, DEFAULT_VISIBLE_TOKENS);
  const visibleTransfers = showAllTransfers
    ? wallet.recentTransfers
    : wallet.recentTransfers.slice(0, DEFAULT_VISIBLE_TRANSFERS);

  const hasMoreTokens = wallet.tokens.length > DEFAULT_VISIBLE_TOKENS;
  const hasMoreTransfers =
    wallet.recentTransfers.length > DEFAULT_VISIBLE_TRANSFERS;

  // Quick actions for this wallet
  const quickActions: QuickAction[] = [
    {
      label: "Profile wallet",
      icon: <UserIcon className="size-3" />,
      prompt: promptGenerators.analyzeWallet(wallet.address),
    },
    {
      label: "DeFi exposure",
      icon: <BanknoteIcon className="size-3" />,
      prompt: promptGenerators.defiExposure(wallet.address, chain.name),
    },
    {
      label: "Any NFTs?",
      icon: <ImageIcon className="size-3" />,
      prompt: promptGenerators.checkNfts(wallet.address),
    },
    {
      label: "Risk score",
      icon: <ShieldCheckIcon className="size-3" />,
      prompt: promptGenerators.riskAssessment(wallet.address),
    },
  ];

  // Header content (always visible when collapsed)
  const headerContent = (
    <div
      className={cn(
        "flex items-center justify-between border-b bg-background/50 p-4 pr-12",
        chain.bgGradient
      )}
    >
      <div className="flex items-center gap-3">
        <div
          className="flex size-10 items-center justify-center rounded-full"
          style={{ backgroundColor: `${chain.color}20` }}
        >
          <WalletIcon className="size-5" style={{ color: chain.color }} />
        </div>
        <div>
          <div className="flex items-center gap-2">
            <AddressDisplay
              address={wallet.address}
              chainId={chainId}
              showCopy
              showExplorerLink
              size="md"
            />
            <ChainBadge chainId={chainId} size="sm" />
          </div>
          {wallet.totalUsdValue && (
            <div className="text-muted-foreground text-sm">
              Total: {formatUsdValue(wallet.totalUsdValue)}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <CollapsibleCard
      cardId={cardId}
      className={cn("bg-gradient-to-br", chain.bgGradient)}
      headerContent={headerContent}
    >
      {/* Two-column layout for tokens and transfers */}
      <div className="grid gap-0 border-b md:grid-cols-2">
        {/* Tokens column */}
        <div className="border-b bg-background/30 md:border-r md:border-b-0">
          <div className="flex items-center justify-between border-b bg-background/30 px-4 py-2">
            <div className="flex items-center gap-2 font-medium text-sm">
              <CoinsIcon className="size-4" />
              <span>Tokens ({wallet.tokens.length})</span>
            </div>
          </div>
          <div className="divide-y">
            {visibleTokens.map((token, idx) => (
              <TokenRow
                chainId={chainId}
                key={`${token.token.address}-${idx}`}
                onQuickAction={onQuickAction}
                token={token}
                walletAddress={wallet.address}
              />
            ))}
            {wallet.tokens.length === 0 && (
              <div className="p-4 text-center text-muted-foreground text-sm">
                No tokens found
              </div>
            )}
          </div>
          {hasMoreTokens && (
            <button
              className="w-full border-t bg-muted/30 p-2 text-center text-primary text-xs hover:bg-muted/50"
              onClick={() => setShowAllTokens(!showAllTokens)}
              type="button"
            >
              {showAllTokens
                ? "Show less"
                : `+${wallet.tokens.length - DEFAULT_VISIBLE_TOKENS} more tokens`}
            </button>
          )}
        </div>

        {/* Transfers column */}
        <div className="bg-background/30">
          <div className="flex items-center justify-between border-b bg-background/30 px-4 py-2">
            <div className="flex items-center gap-2 font-medium text-sm">
              <ArrowUpRightIcon className="size-4" />
              <span>Recent Transfers ({wallet.recentTransfers.length})</span>
            </div>
          </div>
          <div className="divide-y">
            {visibleTransfers.map((transfer, idx) => (
              <TransferRow
                chainId={chainId}
                key={`${transfer.from}-${transfer.to}-${transfer.token.address}-${transfer.value}-${idx}`}
                onQuickAction={onQuickAction}
                transfer={transfer}
                walletAddress={wallet.address}
              />
            ))}
            {wallet.recentTransfers.length === 0 && (
              <div className="p-4 text-center text-muted-foreground text-sm">
                No recent transfers
              </div>
            )}
          </div>
          {hasMoreTransfers && (
            <button
              className="w-full border-t bg-muted/30 p-2 text-center text-primary text-xs hover:bg-muted/50"
              onClick={() => setShowAllTransfers(!showAllTransfers)}
              type="button"
            >
              {showAllTransfers
                ? "Show less"
                : `+${wallet.recentTransfers.length - DEFAULT_VISIBLE_TRANSFERS} more transfers`}
            </button>
          )}
        </div>
      </div>

      {/* Quick actions */}
      <div className="bg-background/50 p-4">
        <QuickActionsBar
          actions={quickActions}
          onAction={onQuickAction}
          size="sm"
        />
      </div>
    </CollapsibleCard>
  );
}

/**
 * Token row in the wallet overview
 */
type TokenRowProps = {
  token: TokenBalance;
  walletAddress: string;
  chainId: string;
  onQuickAction: WalletOverviewProps["onQuickAction"];
};

function TokenRow({
  token,
  walletAddress,
  chainId,
  onQuickAction,
}: TokenRowProps) {
  const [hovered, setHovered] = useState(false);

  const formattedBalance = formatTokenValue(
    token.balance,
    token.token.decimals,
    4
  );

  return (
    <div
      className="group flex items-center justify-between p-3 transition-colors hover:bg-muted/30"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className="flex items-center gap-3">
        {/* Token icon */}
        <div className="flex size-8 items-center justify-center rounded-full bg-muted">
          {token.token.iconUrl ? (
            <img
              alt={token.token.symbol}
              className="size-6 rounded-full"
              src={token.token.iconUrl}
            />
          ) : (
            <span className="font-bold text-muted-foreground text-xs">
              {token.token.symbol.slice(0, 2)}
            </span>
          )}
        </div>
        <div>
          <div className="font-medium text-sm">{token.token.symbol}</div>
          <div className="text-muted-foreground text-xs">
            {token.token.name}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <div className="text-right">
          <div className="font-medium text-sm tabular-nums">
            {formattedBalance}
          </div>
          {token.usdValue && (
            <div className="text-muted-foreground text-xs">
              {formatUsdValue(token.usdValue)}
            </div>
          )}
        </div>

        {/* Quick action on hover */}
        {hovered && (
          <QuickActionButton
            className="ml-2"
            label="Ask"
            onClick={() =>
              onQuickAction(
                promptGenerators.aboutToken(
                  token.token.address,
                  token.token.symbol
                )
              )
            }
            showArrow
            size="sm"
            variant="subtle"
          />
        )}
      </div>
    </div>
  );
}

/**
 * Transfer row in the wallet overview
 */
type TransferRowProps = {
  transfer: TokenTransfer;
  walletAddress: string;
  chainId: string;
  onQuickAction: WalletOverviewProps["onQuickAction"];
};

function TransferRow({
  transfer,
  walletAddress,
  chainId,
  onQuickAction,
}: TransferRowProps) {
  const [hovered, setHovered] = useState(false);

  const isOutgoing =
    transfer.from.toLowerCase() === walletAddress.toLowerCase();
  const DirectionIcon = isOutgoing ? ArrowUpRightIcon : ArrowDownLeftIcon;
  const directionColor = isOutgoing ? "text-destructive" : "text-accent";

  const formattedValue = formatTokenValue(
    transfer.value,
    transfer.token.decimals,
    4
  );

  const counterparty = isOutgoing ? transfer.to : transfer.from;

  return (
    <div
      className="group flex items-center justify-between p-3 transition-colors hover:bg-muted/30"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className="flex items-center gap-3">
        <div
          className={cn(
            "flex size-8 items-center justify-center rounded-full",
            isOutgoing ? "bg-destructive/10" : "bg-accent/10"
          )}
        >
          <DirectionIcon className={cn("size-4", directionColor)} />
        </div>
        <div>
          <div className="flex items-center gap-1 text-sm">
            <span className={cn("font-medium tabular-nums", directionColor)}>
              {isOutgoing ? "-" : "+"}
              {formattedValue}
            </span>
            <span className="text-muted-foreground">
              {transfer.token.symbol}
            </span>
          </div>
          <div className="flex items-center gap-1 text-muted-foreground text-xs">
            <span>{isOutgoing ? "to" : "from"}</span>
            <span className="font-mono">
              {truncateAddress(counterparty, 4, 4)}
            </span>
          </div>
        </div>
      </div>

      {/* Quick action on hover */}
      {hovered && (
        <QuickActionButton
          label="Ask"
          onClick={() =>
            onQuickAction(
              promptGenerators.aboutAddress(
                counterparty,
                isOutgoing
                  ? "received tokens from this wallet"
                  : "sent tokens to this wallet"
              )
            )
          }
          showArrow
          size="sm"
          variant="subtle"
        />
      )}
    </div>
  );
}

/**
 * Skeleton loader for wallet overview
 */
function WalletOverviewSkeleton({
  addresses,
  chainId,
}: {
  addresses?: string[];
  chainId: string;
}) {
  const chain = getChainInfo(chainId);
  const addressList = addresses ?? [];

  if (addressList.length === 0) {
    return (
      <div className="flex items-center justify-center gap-2 rounded-xl border bg-background/30 p-6">
        <div className="size-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        <span className="text-muted-foreground text-sm">
          Fetching wallet state...
        </span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {addressList.map((address, idx) => (
        <div
          className={cn(
            "overflow-hidden rounded-xl border bg-gradient-to-br",
            chain.bgGradient
          )}
          key={address || idx}
        >
          {/* Header skeleton */}
          <div className="flex items-center justify-between border-b bg-background/50 p-4">
            <div className="flex items-center gap-3">
              <Skeleton className="size-10 rounded-full" />
              <div className="space-y-2">
                <Skeleton className="h-4 w-40" />
                <Skeleton className="h-3 w-24" />
              </div>
            </div>
          </div>

          {/* Loading indicator */}
          <div className="flex items-center justify-center gap-2 bg-background/30 p-6">
            <div className="size-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            <span className="text-muted-foreground text-sm">
              Fetching wallet state...
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Error state for wallet overview
 */
function WalletOverviewError({
  error,
  addresses,
  chainId,
}: {
  error: string;
  addresses?: string[];
  chainId: string;
}) {
  const addressList = addresses ?? [];
  return (
    <div className="rounded-xl border border-destructive/30 bg-destructive/10 p-4">
      <div className="flex items-start gap-3">
        <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-destructive/20">
          <WalletIcon className="size-4 text-destructive" />
        </div>
        <div className="flex-1">
          <h4 className="font-medium text-destructive">
            Failed to fetch wallet state
          </h4>
          <p className="mt-1 text-destructive/80 text-sm">{error}</p>
          {addressList.length > 0 && (
            <div className="mt-2 text-destructive/70 text-xs">
              Wallet: {addressList[0]}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
